import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  CogIcon, 
  PlusIcon, 
  PlayIcon, 
  PauseIcon, 
  StopIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ArrowRightIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  DocumentDuplicateIcon,
  SparklesIcon,
  FunnelIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  UserGroupIcon,
  BoltIcon,
  ShareIcon,
  ArrowPathIcon,
  Squares2X2Icon,
  QueueListIcon,
  BeakerIcon,
  CodeBracketIcon,
  ShieldCheckIcon,
  ArrowDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import { workflowsService } from '@/services/workflows';
import { agentsService } from '@/services/agents';
import { templatesService, type TemplateInfo } from '@/services/templates';
import { Agent, Workflow, WorkflowStep } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import toast from 'react-hot-toast';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useNavigate } from 'react-router-dom';

// Workflow Pattern Types
type WorkflowPattern = 
  | 'sequential_handoff'  // Changed from 'sequential' to match backend
  | 'hub-spoke' 
  | 'parallel' 
  | 'agent-tool' 
  | 'conditional';

// Extended template interface for frontend compatibility
interface WorkflowTemplate extends TemplateInfo {
  complexity: 'low' | 'medium' | 'high';
  estimated_time: string;
  icon: React.ComponentType<{ className?: string }>;
  pattern: WorkflowPattern;
  agents: Array<{
    name: string;
    role: string;
    tools: string[];
    instructions: string;
  }>;
  workflow_steps: Array<{
    order: number;
    agent_role: string;
    timeout: number;
    handoff_to?: string;
    condition?: string;
  }>;
  integration_requirements: string[];
}

interface CreateWorkflowModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedTemplate?: WorkflowTemplate;
  availableAgents: Agent[];
  workspaceId: string;
}

const CreateWorkflowModal: React.FC<CreateWorkflowModalProps> = ({ 
  isOpen, 
  onClose, 
  selectedTemplate,
  availableAgents,
  workspaceId 
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [workflowData, setWorkflowData] = useState({
    name: selectedTemplate?.name || '',
    description: selectedTemplate?.description || '',
    pattern: selectedTemplate?.pattern || 'sequential_handoff' as WorkflowPattern,
    steps: [] as WorkflowStep[],
    agents: [] as string[],
    guardrails: [] as string[],
    triggers: [] as string[],
    timeout: 1800 // 30 minutes default
  });

  const [selectedAgents, setSelectedAgents] = useState<Agent[]>([]);
  const [handoffChain, setHandoffChain] = useState<Array<{ from: string; to: string; condition?: string }>>([]);

  const queryClient = useQueryClient();

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setWorkflowData({
        name: selectedTemplate?.name || '',
        description: selectedTemplate?.description || '',
        pattern: selectedTemplate?.pattern || 'sequential_handoff' as WorkflowPattern,
        steps: [],
        agents: [],
        guardrails: [],
        triggers: [],
        timeout: 1800
      });
      setSelectedAgents([]);
      setHandoffChain([]);
      setIsLoading(false);
    }
  }, [isOpen, selectedTemplate]);

  if (!isOpen) return null;

  const workflowPatterns = [
    {
      id: 'sequential_handoff',
      name: 'Sequential Handoff',
      description: 'Linear workflow: Agent A → Agent B → Agent C',
      icon: ArrowRightIcon,
      example: 'Code Review → Security Scan → Deployment',
      complexity: 'low'
    },
    {
      id: 'hub-spoke',
      name: 'Hub-and-Spoke',
      description: 'Central coordinator with specialist agents',
      icon: Squares2X2Icon,
      example: 'Project Manager → [Dev Team, QA Team, DevOps]',
      complexity: 'medium'
    },
    {
      id: 'parallel',
      name: 'Parallel Execution',
      description: 'Multiple agents working simultaneously',
      icon: UserGroupIcon,
      example: 'Code Analysis + Security Scan + Test Generation',
      complexity: 'medium'
    },
    {
      id: 'agent-tool',
      name: 'Agent-as-Tool',
      description: 'Agents used as callable tools within workflows',
      icon: CodeBracketIcon,
      example: 'Main Agent using Analysis Agent as a tool',
      complexity: 'high'
    },
    {
      id: 'conditional',
      name: 'Conditional Handoff',
      description: 'Dynamic routing based on context and conditions',
      icon: ShareIcon,
      example: 'Triage → [Bug Fix, Feature Request, Support]',
      complexity: 'high'
    }
  ];

  const handlePatternSelect = (pattern: WorkflowPattern) => {
    setWorkflowData(prev => ({ ...prev, pattern }));
  };

  const handleAgentSelect = (agent: Agent) => {
    if (selectedAgents.find(a => a.id === agent.id)) {
      setSelectedAgents(prev => prev.filter(a => a.id !== agent.id));
    } else {
      setSelectedAgents(prev => [...prev, agent]);
    }
  };

  const handleHandoffAdd = () => {
    if (selectedAgents.length >= 2) {
      setHandoffChain(prev => [...prev, { from: '', to: '', condition: '' }]);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Choose Workflow Pattern
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {workflowPatterns.map((pattern) => {
                  const Icon = pattern.icon;
                  return (
                    <button
                      key={pattern.id}
                      onClick={() => handlePatternSelect(pattern.id as WorkflowPattern)}
                      className={`p-4 border-2 rounded-lg text-left transition-all duration-200 ${
                        workflowData.pattern === pattern.id
                          ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <Icon className="h-6 w-6 text-indigo-600 dark:text-indigo-400 mt-1" />
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 dark:text-white">
                            {pattern.name}
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {pattern.description}
                          </p>
                          <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-2">
                            Example: {pattern.example}
                          </p>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mt-2 ${
                            pattern.complexity === 'low' 
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                              : pattern.complexity === 'medium'
                              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                              : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                          }`}>
                            {pattern.complexity} complexity
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Basic Information
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Workflow Name
                  </label>
                  <input
                    type="text"
                    value={workflowData.name}
                    onChange={(e) => setWorkflowData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    placeholder="Enter workflow name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={workflowData.description}
                    onChange={(e) => setWorkflowData(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    placeholder="Describe what this workflow does"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Timeout (seconds)
                  </label>
                  <input
                    type="number"
                    value={workflowData.timeout}
                    onChange={(e) => setWorkflowData(prev => ({ ...prev, timeout: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    min="60"
                    max="3600"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Select Agents
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
                {availableAgents.map((agent) => (
                  <div
                    key={agent.id}
                    onClick={() => handleAgentSelect(agent)}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                      selectedAgents.find(a => a.id === agent.id)
                        ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`p-2 rounded-lg ${agent.is_active ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-700'}`}>
                        <CogIcon className={`h-5 w-5 ${agent.is_active ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`} />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {agent.name}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {agent.description}
                        </p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {agent.tools && agent.tools.slice(0, 3).map((tool) => (
                            <span
                              key={tool}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300"
                            >
                              {tool}
                            </span>
                          ))}
                          {agent.tools && agent.tools.length > 3 && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              +{agent.tools.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Configure Handoffs & Flow
              </h3>
              
              {workflowData.pattern === 'sequential_handoff' && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Configure the order of agent execution. Each agent will hand off to the next in sequence.
                  </p>
                  
                  {/* Automatic handoff instructions notice */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                    <div className="flex items-start space-x-2">
                      <CheckCircleIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                      <div className="text-sm">
                        <p className="text-blue-800 dark:text-blue-200 font-medium">Automatic Handoff Setup</p>
                        <p className="text-blue-700 dark:text-blue-300 mt-1">
                          Handoff instructions will be automatically added to each agent's instructions, ensuring proper workflow execution between agents.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    {selectedAgents.map((agent, index) => (
                      <div key={agent.id} className="flex items-center space-x-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <span className="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
                          {index + 1}
                        </span>
                        <div className="flex-1">
                          <span className="font-medium text-gray-900 dark:text-white">{agent.name}</span>
                          {index < selectedAgents.length - 1 && (
                            <span className="text-xs text-gray-500 dark:text-gray-400 block mt-1">
                              Will hand off to: {selectedAgents[index + 1].name}
                            </span>
                          )}
                          {index === selectedAgents.length - 1 && (
                            <span className="text-xs text-green-600 dark:text-green-400 block mt-1">
                              Final step - completes workflow
                            </span>
                          )}
                        </div>
                        {index < selectedAgents.length - 1 && (
                          <ArrowRightIcon className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {workflowData.pattern === 'hub-spoke' && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Select a coordinator agent and the specialist agents it will delegate to.
                  </p>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Coordinator Agent
                      </label>
                      <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                        <option value="">Select coordinator...</option>
                        {selectedAgents.map(agent => (
                          <option key={agent.id} value={agent.id}>{agent.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Specialist Agents
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {selectedAgents.map(agent => (
                          <div key={agent.id} className="flex items-center space-x-2 p-2 bg-gray-50 dark:bg-gray-800 rounded">
                            <input type="checkbox" className="rounded text-indigo-600 focus:ring-indigo-500 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600" />
                            <span className="text-sm text-gray-900 dark:text-white">{agent.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {workflowData.pattern === 'parallel' && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    All selected agents will execute simultaneously. Results will be aggregated.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedAgents.map(agent => (
                      <div key={agent.id} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <BoltIcon className="h-5 w-5 text-yellow-500" />
                          <span className="font-medium text-gray-900 dark:text-white">{agent.name}</span>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                          Executes in parallel
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {workflowData.pattern === 'conditional' && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Configure conditional handoffs based on context and rules.
                  </p>
                  <div className="space-y-3">
                    {handoffChain.map((handoff, index) => (
                      <div key={index} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                              From Agent
                            </label>
                            <select className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                              <option value="">Select...</option>
                              {selectedAgents.map(agent => (
                                <option key={agent.id} value={agent.id}>{agent.name}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                              To Agent
                            </label>
                            <select className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                              <option value="">Select...</option>
                              {selectedAgents.map(agent => (
                                <option key={agent.id} value={agent.id}>{agent.name}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Condition
                            </label>
                            <input
                              type="text"
                              placeholder="e.g., priority='high'"
                              className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                    <button
                      onClick={handleHandoffAdd}
                      className="w-full p-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500 transition-colors bg-white dark:bg-gray-800"
                    >
                      + Add Handoff Rule
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Guardrails & Safety
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Input Guardrails
                  </label>
                  <div className="space-y-2">
                    {[
                      'Content moderation',
                      'Input validation',
                      'Rate limiting',
                      'Authentication check'
                    ].map((guardrail) => (
                      <label key={guardrail} className="flex items-center space-x-2">
                        <input type="checkbox" className="rounded text-indigo-600 focus:ring-indigo-500 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600" />
                        <span className="text-sm text-gray-900 dark:text-white">{guardrail}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Output Guardrails
                  </label>
                  <div className="space-y-2">
                    {[
                      'PII detection',
                      'Content filtering',
                      'Quality validation',
                      'Format verification'
                    ].map((guardrail) => (
                      <label key={guardrail} className="flex items-center space-x-2">
                        <input type="checkbox" className="rounded text-indigo-600 focus:ring-indigo-500 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600" />
                        <span className="text-sm text-gray-900 dark:text-white">{guardrail}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const validateWorkflowData = (): string[] => {
    const errors: string[] = [];
    
    if (!workflowData.name.trim()) {
      errors.push('Workflow name is required');
    }
    
    if (!workspaceId) {
      errors.push('Workspace ID is required');
    }
    
    if (selectedAgents.length === 0) {
      errors.push('At least one agent must be selected');
    }
    
    if (workflowData.pattern === 'sequential_handoff' && selectedAgents.length < 2) {
      errors.push('Sequential workflows require at least 2 agents');
    }
    
    return errors;
  };

  const buildWorkflowSteps = (): Array<{
    order: number;
    agent_id: number;
    timeout: number;
    conditions: Record<string, any>;
    retry_count: number;
  }> => {
    return selectedAgents.map((agent, index) => ({
      order: index + 1,
      agent_id: agent.id,
      timeout: workflowData.timeout,
      conditions: {},
      retry_count: 3
    }));
  };

  const updateAgentHandoffs = async (): Promise<void> => {
    // Update agent handoffs based on the workflow pattern
    if (workflowData.pattern === 'sequential_handoff' && selectedAgents.length > 1) {
      // For sequential workflows, each agent hands off to the next
      for (let i = 0; i < selectedAgents.length - 1; i++) {
        const currentAgent = selectedAgents[i];
        const nextAgent = selectedAgents[i + 1];
        
        try {
          // Generate the handoff tool name (backend format)
          const handoffToolName = `transfer_to_${nextAgent.name.toLowerCase().replace(/\s+/g, '_')}`;
          
          // Create enhanced instructions with explicit handoff tool usage
          const handoffInstructions = `

## MANDATORY HANDOFF ##
After completing your work, you MUST use the ${handoffToolName} tool to hand off to ${nextAgent.name}.

Example handoff message:
"I have completed my analysis/work for this task.
[Include relevant links, context, and findings here]
${nextAgent.name}, please proceed with your specialized tasks based on this work."

## CRITICAL RULE ##
You MUST use the ${handoffToolName} tool after completing your work. Do not consider your task complete until you have successfully handed off to ${nextAgent.name}.`;

          // Get current agent instructions and append handoff instructions if not already present
          const currentInstructions = currentAgent.instructions || '';
          let updatedInstructions = currentInstructions;
          
          // Only add handoff instructions if they don't already exist
          if (!currentInstructions.includes('MANDATORY HANDOFF') && !currentInstructions.includes(handoffToolName)) {
            updatedInstructions = currentInstructions + handoffInstructions;
          }

          // Update the agent's handoffs and instructions
          await agentsService.updateAgent(currentAgent.id, {
            handoffs: [nextAgent.name],
            instructions: updatedInstructions
          });
          
          console.log(`✅ Updated handoffs for ${currentAgent.name} → ${nextAgent.name}`);
          
        } catch (error) {
          console.error(`Error updating handoffs for agent ${currentAgent.name}:`, error);
          // Continue with other agents even if one fails
        }
      }
      
      // For the final agent, ensure it has instructions that it's the final step
      if (selectedAgents.length > 0) {
        const finalAgent = selectedAgents[selectedAgents.length - 1];
        try {
          const finalInstructions = `

## FINAL STEP ##
This is the final step in the workflow. After completing your work, provide comprehensive results and complete the workflow. No handoff is required.`;

          const currentInstructions = finalAgent.instructions || '';
          let updatedInstructions = currentInstructions;
          
          // Only add final step instructions if they don't already exist
          if (!currentInstructions.includes('FINAL STEP') && !currentInstructions.includes('final step in the workflow')) {
            updatedInstructions = currentInstructions + finalInstructions;
          }

          await agentsService.updateAgent(finalAgent.id, {
            instructions: updatedInstructions,
            handoffs: [] // Final agent has no handoffs
          });
          
          console.log(`✅ Updated final agent: ${finalAgent.name}`);
          
        } catch (error) {
          console.error(`Error updating final agent ${finalAgent.name}:`, error);
        }
      }
    }
  };

  const handleCreateWorkflow = async (): Promise<void> => {
    const errors = validateWorkflowData();
    if (errors.length > 0) {
      toast.error(errors.join('. '));
      return;
    }

    setIsLoading(true);
    
    try {
      // Build workflow steps from selected agents
      const steps = buildWorkflowSteps();
      
      // Prepare workflow data for API
      const workflowPayload = {
        name: workflowData.name.trim(),
        description: workflowData.description?.trim() || '',
        workspace_id: workspaceId!,
        pattern: workflowData.pattern,
        orchestrator_name: workflowData.pattern === 'sequential_handoff' ? `${workflowData.name.trim()} Orchestrator` : undefined,
        steps,
        triggers: workflowData.triggers.length > 0 ? workflowData.triggers : ['manual', 'api'],
        settings: {
          max_execution_time: workflowData.timeout * 2, // Double the step timeout for total workflow
          parallel_execution: workflowData.pattern === 'parallel',
          auto_retry: true,
          max_retries: 3,
          notification_settings: {
            on_success: true,
            on_failure: true,
            on_timeout: true
          },
          orchestrator_entry_point: workflowData.pattern === 'sequential_handoff',
          handoff_pattern: workflowData.pattern === 'sequential_handoff' ? 'sequential_with_orchestrator' : workflowData.pattern
        },
        is_active: true
      };

      // Create the workflow
      const createdWorkflow = await workflowsService.createWorkflow(workflowPayload);
      
      // Update agent handoffs for sequential workflows
      if (workflowData.pattern === 'sequential_handoff') {
        await updateAgentHandoffs();
      }
      
      // Invalidate queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      queryClient.invalidateQueries({ queryKey: ['workflows', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      queryClient.invalidateQueries({ queryKey: ['agents', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      
      toast.success(`Workflow "${createdWorkflow.name}" created successfully!`);
      onClose();
      
    } catch (error: any) {
      console.error('Error creating workflow:', error);
      toast.error(error.message || 'Failed to create workflow');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStepNavigation = (direction: 'next' | 'prev'): void => {
    if (direction === 'next' && currentStep < 5) {
      // Validate current step before proceeding
      if (currentStep === 1 && !workflowData.pattern) {
        toast.error('Please select a workflow pattern');
        return;
      }
      if (currentStep === 2 && !workflowData.name.trim()) {
        toast.error('Please enter a workflow name');
        return;
      }
      if (currentStep === 3 && selectedAgents.length === 0) {
        toast.error('Please select at least one agent');
        return;
      }
      setCurrentStep(currentStep + 1);
    } else if (direction === 'prev' && currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-80" onClick={onClose} />
        
        <div className="inline-block w-full max-w-4xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-lg border border-gray-200 dark:border-gray-700">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Create New Workflow
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Step {currentStep} of 5
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <span className="sr-only">Close</span>
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              {[1, 2, 3, 4, 5].map((step) => (
                <div
                  key={step}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step <= currentStep
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                  }`}
                >
                  {step}
                </div>
              ))}
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / 5) * 100}%` }}
              />
            </div>
          </div>

          {/* Step Content */}
          <div className="mb-8">
            {renderStepContent()}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => handleStepNavigation('prev')}
              disabled={currentStep === 1}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              {currentStep < 5 ? (
                <button
                  onClick={() => handleStepNavigation('next')}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={handleCreateWorkflow}
                  disabled={isLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  {isLoading ? 'Creating...' : 'Create Workflow'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Add WorkflowFlowChart component after the interfaces
interface WorkflowFlowChartProps {
  workflow: Workflow;
  agents: Agent[];
}

const WorkflowFlowChart: React.FC<WorkflowFlowChartProps> = ({ workflow, agents }) => {
  // Get agents involved in this workflow sorted by step order
  const workflowAgents = workflow.steps
    .sort((a, b) => a.order - b.order)
    .map(step => agents.find(agent => agent.id === step.agent_id))
    .filter(Boolean) as Agent[];

  if (workflowAgents.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500 dark:text-gray-400">
        <ExclamationTriangleIcon className="h-8 w-8 mx-auto mb-2" />
        <p>No agents configured for this workflow</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Orchestrator */}
      {workflow.orchestrator_name && (
        <div className="flex flex-col items-center">
          <div className="bg-purple-100 dark:bg-purple-900/30 border-2 border-purple-300 dark:border-purple-700 rounded-lg p-4 min-w-[200px]">
            <div className="flex items-center space-x-2">
              <Squares2X2Icon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              <div>
                <div className="font-semibold text-purple-900 dark:text-purple-100">
                  {workflow.orchestrator_name}
                </div>
                <div className="text-xs text-purple-700 dark:text-purple-300">
                  Orchestrator
                </div>
              </div>
            </div>
          </div>
          {workflowAgents.length > 0 && (
            <div className="flex flex-col items-center mt-2">
              <ArrowDownIcon className="h-6 w-6 text-gray-400" />
              <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Routes to first agent
              </span>
            </div>
          )}
        </div>
      )}

      {/* Agent Flow */}
      <div className="space-y-4">
        {workflowAgents.map((agent, index) => (
          <div key={agent.id} className="flex flex-col items-center">
            {/* Agent Card */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm min-w-[300px]">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
                    {index + 1}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <h4 className="font-semibold text-gray-900 dark:text-white">
                      {agent.name}
                    </h4>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      agent.is_active
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                    }`}>
                      {agent.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                    {agent.description}
                  </p>
                  
                  {/* Tools */}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {agent.tools.slice(0, 3).map((tool) => (
                      <span
                        key={tool}
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300"
                      >
                        {tool}
                      </span>
                    ))}
                    {agent.tools.length > 3 && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        +{agent.tools.length - 3} tools
                      </span>
                    )}
                  </div>

                  {/* Handoffs */}
                  {agent.handoffs && agent.handoffs.length > 0 && (
                    <div className="mt-2">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                        Can hand off to:
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {agent.handoffs.map((handoffName) => (
                          <span
                            key={handoffName}
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300"
                          >
                            {handoffName}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Handoff Arrow */}
            {index < workflowAgents.length - 1 && (
              <div className="flex flex-col items-center my-3">
                <ArrowDownIcon className="h-6 w-6 text-gray-400" />
                <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {workflow.pattern === 'sequential_handoff' ? 'Sequential handoff' : 'Handoff'}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Workflow Summary */}
      <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <h5 className="font-semibold text-gray-900 dark:text-white mb-2">Workflow Summary</h5>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-gray-600 dark:text-gray-400">Pattern:</span>
            <span className="ml-2 font-medium text-gray-900 dark:text-white capitalize">
              {workflow.pattern?.replace('_', ' ') || 'Sequential'}
            </span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Agents:</span>
            <span className="ml-2 font-medium text-gray-900 dark:text-white">
              {workflowAgents.length}
            </span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">Executions:</span>
            <span className="ml-2 font-medium text-gray-900 dark:text-white">
              {workflow.execution_count || 0}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const Workflows: React.FC = () => {
  const { user } = useAuth();
  const { workspaces, currentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<'workflows' | 'templates'>('workflows');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<WorkflowTemplate | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState<'name' | 'created' | 'updated' | 'runs'>('updated');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [expandedWorkflows, setExpandedWorkflows] = useState<Set<number>>(new Set());
  const [workflowToDelete, setWorkflowToDelete] = useState<Workflow | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [templateToApply, setTemplateToApply] = useState<WorkflowTemplate | null>(null);
  const [showTemplateConfirm, setShowTemplateConfirm] = useState(false);

  // Toggle workflow expansion
  const toggleWorkflowExpansion = (workflowId: number) => {
    setExpandedWorkflows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(workflowId)) {
        newSet.delete(workflowId);
      } else {
        newSet.add(workflowId);
      }
      return newSet;
    });
  };

  // Handle delete workflow
  const handleDeleteWorkflow = (workflow: Workflow) => {
    setWorkflowToDelete(workflow);
    setShowDeleteConfirm(true);
  };

  // Handle template application with confirmation
  const handleApplyTemplate = (template: WorkflowTemplate) => {
    setTemplateToApply(template);
    setShowTemplateConfirm(true);
  };

  // Confirm template application
  const confirmTemplateApplication = () => {
    if (templateToApply) {
      applyTemplateMutation.mutate(templateToApply);
      setShowTemplateConfirm(false);
      setTemplateToApply(null);
    }
  };

  const { data: workflowsResponse, isLoading, refetch } = useQuery({
    queryKey: ['workflows', currentWorkspace?.id],
    queryFn: () => workflowsService.getWorkflows(currentWorkspace?.id!),
    enabled: !!currentWorkspace?.id,
  });

  // Extract workflows array from paginated response
  const workflows = workflowsResponse?.items || [];

  const { data: agentsResponse } = useQuery({
    queryKey: ['agents', currentWorkspace?.id],
    queryFn: () => agentsService.getAgents({ workspace_id: currentWorkspace?.id! }),
    enabled: !!currentWorkspace?.id,
  });

  // Extract agents array from paginated response
  const agents = agentsResponse?.agents || [];

  const deleteWorkflowMutation = useMutation({
    mutationFn: workflowsService.deleteWorkflow,
    onSuccess: () => {
      toast.success('Workflow deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      setShowDeleteConfirm(false);
      setWorkflowToDelete(null);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete workflow');
    },
  });

  // Fetch templates from backend - always fetch for count, but with different stale times
  const { data: templatesData, isLoading: isLoadingTemplates, error: templatesError } = useQuery({
    queryKey: ['templates'],
    queryFn: () => templatesService.listTemplates(),
    staleTime: activeTab === 'templates' ? 5 * 60 * 1000 : 60 * 1000, // 5 min when active, 1 min when not
    refetchOnWindowFocus: activeTab === 'templates',
  });

  // Map backend templates to frontend format
  const getIconForCategory = (category: string) => {
    switch (category.toLowerCase()) {
      case 'software development':
        return CodeBracketIcon;
      case 'business process':
        return DocumentDuplicateIcon;
      case 'content & marketing':
        return SparklesIcon;
      case 'quality assurance':
        return ShieldCheckIcon;
      default:
        return BoltIcon;
    }
  };

  const mapBackendTemplateToFrontend = (template: TemplateInfo): WorkflowTemplate => {
    return {
      ...template,
      complexity: template.difficulty === 'beginner' ? 'low' : 
                 template.difficulty === 'intermediate' ? 'medium' : 'high',
      estimated_time: template.estimated_setup_time,
      icon: getIconForCategory(template.category),
      pattern: 'sequential_handoff' as WorkflowPattern, // All our backend templates use this pattern
      agents: [], // We'll populate this when needed
      workflow_steps: [], // We'll populate this when needed
      integration_requirements: template.tags.filter(tag => 
        ['jira', 'confluence', 'github'].includes(tag.toLowerCase())
      )
    };
  };

  const workflowTemplates: WorkflowTemplate[] = templatesData?.templates.map(mapBackendTemplateToFrontend) || [];

  // Mutation for applying templates
  const applyTemplateMutation = useMutation({
    mutationFn: async (template: WorkflowTemplate) => {
      if (!currentWorkspace) {
        throw new Error('No workspace selected');
      }
      
      // First validate the template
      const validation = await templatesService.validateTemplate({
        template_id: template.id,
        workspace_id: currentWorkspace.id
      });
      
      if (!validation.can_apply && validation.conflicts.length > 0) {
        // Show conflicts and ask user if they want to force apply
        const shouldForce = window.confirm(
          `Template conflicts detected:\n${validation.conflicts.join('\n')}\n\nDo you want to apply anyway?`
        );
        
        if (!shouldForce) {
          throw new Error('Template application cancelled due to conflicts');
        }
      }
      
      // Apply the template
      return await templatesService.applyTemplate({
        template_id: template.id,
        workspace_id: currentWorkspace.id,
        force: validation.conflicts.length > 0
      });
    },
    onSuccess: (result, template) => {
      if (result.success) {
        toast.success(`Template "${template.name}" applied successfully! Created ${result.agents_created} agents and ${result.workflows_created} workflows.`);
        // Refresh the workflows and agents lists
        queryClient.invalidateQueries({ queryKey: ['workflows'] });
        queryClient.invalidateQueries({ queryKey: ['agents'] });
      } else {
        toast.error(result.message || 'Failed to apply template');
      }
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to apply template');
    },
  });

  const handleCreateFromTemplate = async (template: WorkflowTemplate) => {
    if (!currentWorkspace) {
      toast.error('Please select a workspace first');
      return;
    }
    
    // Show loading toast
    const loadingToast = toast.loading(`Applying template "${template.name}"...`);
    
    try {
      await applyTemplateMutation.mutateAsync(template);
    } catch (error) {
      // Error is handled in mutation
    } finally {
      toast.dismiss(loadingToast);
    }
  };

  const handleExecuteWorkflow = (workflow: Workflow) => {
    // Redirect to workflow chat interface
    navigate(`/workflows/${workflow.id}/chat`);
  };

  const renderWorkflowCard = (workflow: Workflow) => {
    const isExpanded = expandedWorkflows.has(workflow.id);
    
    return (
      <div key={workflow.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
        <div className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {workflow.name}
                </h3>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  workflow.is_active
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                }`}>
                  {workflow.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              
              {workflow.description && (
                <p className="text-gray-600 dark:text-gray-400 mt-2">
                  {workflow.description}
                </p>
              )}
              
              <div className="flex items-center space-x-4 mt-4 text-sm text-gray-500 dark:text-gray-400">
                <div className="flex items-center space-x-1">
                  <CogIcon className="h-4 w-4" />
                  <span>{workflow.steps?.length || 0} agents</span>
                </div>
                <div className="flex items-center space-x-1">
                  <ShareIcon className="h-4 w-4" />
                  <span className="capitalize">{workflow.pattern?.replace('_', ' ') || 'Sequential'}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <ClockIcon className="h-4 w-4" />
                  <span>{workflow.execution_count || 0} executions</span>
                </div>
                {workflow.last_execution && (
                  <div className="flex items-center space-x-1">
                    <CheckCircleIcon className="h-4 w-4" />
                    <span>Last: {new Date(workflow.last_execution).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => toggleWorkflowExpansion(workflow.id)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title={isExpanded ? "Collapse" : "Expand workflow details"}
              >
                {isExpanded ? (
                  <ChevronUpIcon className="h-5 w-5" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5" />
                )}
              </button>
              
              <button
                onClick={() => handleDeleteWorkflow(workflow)}
                className="p-2 text-red-400 hover:text-red-600 dark:hover:text-red-300 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                title="Delete workflow"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
              
              <button
                onClick={() => handleExecuteWorkflow(workflow)}
                disabled={!workflow.is_active}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  workflow.is_active
                    ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <PlayIcon className="h-4 w-4" />
                  <span>Execute</span>
                </div>
              </button>
            </div>
          </div>
        </div>
        
        {/* Expanded Content - Workflow Details */}
        {isExpanded && (
          <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-gray-50 dark:bg-gray-900">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Workflow Settings */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Configuration
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Pattern:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                      {workflow.pattern?.replace('_', ' ') || 'Sequential'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Orchestrator:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {workflow.orchestrator_name || 'None'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Triggers:</span>
                    <div className="flex space-x-1">
                      {workflow.triggers?.map((trigger) => (
                        <span key={trigger} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                          {trigger}
                        </span>
                      )) || <span className="text-sm text-gray-500">None</span>}
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Created:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {new Date(workflow.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Last Updated:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {new Date(workflow.updated_at).toLocaleDateString()}
                    </span>
                  </div>
                  {workflow.settings && (
                    <div className="pt-3 border-t border-gray-200 dark:border-gray-600">
                      <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Settings</h5>
                      <div className="space-y-2">
                        {Object.entries(workflow.settings).map(([key, value]) => (
                          <div key={key} className="flex justify-between items-center">
                            <span className="text-xs text-gray-600 dark:text-gray-400 capitalize">
                              {key.replace('_', ' ')}:
                            </span>
                            <span className="text-xs font-medium text-gray-900 dark:text-white">
                              {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Workflow Flow Chart */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Agent Flow
                </h4>
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <WorkflowFlowChart workflow={workflow} agents={agents || []} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Workflows</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Create and manage AI agent workflows using OpenAI SDK patterns
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {/* This button is not managed in the new_code, so it will not work as intended */}
            {/* Keeping it as is to avoid breaking existing functionality */}
            {/* <button
              onClick={() => refetchWorkflows()}
              disabled={isWorkflowsLoading}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              <ArrowPathIcon className={`h-4 w-4 ${isWorkflowsLoading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button> */}
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="flex items-center space-x-2 bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors shadow-lg"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Create Workflow</span>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'workflows', name: 'My Workflows', count: isLoading ? '...' : (workflows || []).length },
              { id: 'templates', name: 'Templates', count: isLoadingTemplates ? '...' : workflowTemplates.length }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as 'workflows' | 'templates')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                {tab.name} ({tab.count})
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        {activeTab === 'workflows' && (
          <div className="space-y-4">
            {workflows.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <CogIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  No workflows yet
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Create your first workflow to automate your processes
                </p>
                <button
                  onClick={() => setIsCreateModalOpen(true)}
                  className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Create Workflow
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {(workflows || []).map((workflow) => renderWorkflowCard(workflow))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'templates' && (
          <div>
            {isLoadingTemplates ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 animate-pulse">
                    <div className="flex items-start space-x-3 mb-4">
                      <div className="p-2 bg-gray-200 dark:bg-gray-700 rounded-lg h-10 w-10"></div>
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
                        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : templatesError ? (
              <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <ExclamationTriangleIcon className="h-12 w-12 text-red-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Failed to load templates
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  There was an error loading workflow templates.
                </p>
                <button
                  onClick={() => queryClient.invalidateQueries({ queryKey: ['templates'] })}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Try Again
                </button>
              </div>
            ) : workflowTemplates.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <SparklesIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  No templates available
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Workflow templates will appear here when available.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {workflowTemplates.map((template) => {
                  const Icon = template.icon;
                  const isApplying = applyTemplateMutation.isPending;
                  
                  return (
                    <div
                      key={template.id}
                      className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow ${
                        isApplying ? 'opacity-50 cursor-wait' : 'cursor-pointer'
                      }`}
                      onClick={() => !isApplying && handleApplyTemplate(template)}
                    >
                      <div className="flex items-start space-x-3 mb-4">
                        <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                          <Icon className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {template.name}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {template.description}
                          </p>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">Pattern:</span>
                          <span className="font-medium text-gray-900 dark:text-white capitalize">
                            {template.pattern.replace('-', ' ')}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">Agents:</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {template.agents_count}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">Setup Time:</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {template.estimated_setup_time}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">Author:</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {template.author}
                          </span>
                        </div>
                      </div>

                      <div className="mt-4 flex items-center justify-between">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          template.difficulty === 'beginner' 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                            : template.difficulty === 'intermediate'
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                        }`}>
                          {template.difficulty}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {template.category}
                        </span>
                      </div>

                      {/* Tags */}
                      {template.tags.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1">
                          {template.tags.slice(0, 3).map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                            >
                              {tag}
                            </span>
                          ))}
                          {template.tags.length > 3 && (
                            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                              +{template.tags.length - 3}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Apply button overlay when applying */}
                      {isApplying && (
                        <div className="absolute inset-0 bg-black bg-opacity-20 rounded-lg flex items-center justify-center">
                          <div className="bg-white dark:bg-gray-800 rounded-lg px-4 py-2 shadow-lg">
                            <div className="flex items-center space-x-2">
                              <ArrowPathIcon className="h-4 w-4 animate-spin text-indigo-600" />
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                Applying template...
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Create Workflow Modal */}
        <CreateWorkflowModal
          isOpen={isCreateModalOpen}
          onClose={() => {
            setIsCreateModalOpen(false);
            setSelectedTemplate(null);
          }}
          selectedTemplate={selectedTemplate || undefined}
          availableAgents={agents || []}
          workspaceId={currentWorkspace?.id || ''}
        />

        {/* Delete Confirmation Dialog */}
        {showDeleteConfirm && workflowToDelete && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
              </div>
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
              <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                      Delete Workflow
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to delete the workflow "{workflowToDelete.name}"? This action cannot be undone.
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                        <strong>Note:</strong> The agents used in this workflow will remain in your workspace and can be reused in other workflows.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={() => deleteWorkflowMutation.mutate(workflowToDelete.id)}
                    disabled={deleteWorkflowMutation.isPending}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deleteWorkflowMutation.isPending ? 'Deleting...' : 'Delete'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setWorkflowToDelete(null);
                    }}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-700 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Template Confirmation Dialog */}
        {showTemplateConfirm && templateToApply && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
              </div>
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
              <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900/30 sm:mx-0 sm:h-10 sm:w-10">
                    <SparklesIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                      Apply Template
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to apply the template "{templateToApply.name}"?
                      </p>
                      <div className="mt-3 space-y-2">
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          <strong>This will create:</strong>
                        </p>
                        <ul className="text-sm text-gray-500 dark:text-gray-400 list-disc list-inside space-y-1">
                          <li>{templateToApply.agents_count} new agents</li>
                          <li>1 new workflow</li>
                          <li>Pre-configured agent instructions and tools</li>
                        </ul>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                          <strong>Estimated setup time:</strong> {templateToApply.estimated_setup_time}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={confirmTemplateApplication}
                    disabled={applyTemplateMutation.isPending}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {applyTemplateMutation.isPending ? 'Applying...' : 'Apply Template'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowTemplateConfirm(false);
                      setTemplateToApply(null);
                    }}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-700 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Workflows; 